import {useActiveVkuiLocation, useBlocker, useFirstPageCheck, useRouteNavigator} from "@vkontakte/vk-mini-apps-router";
import {Button, PanelHeader, PanelHeaderBack, PopoutWrapper, ScreenSpinner, Separator, SplitCol, SplitLayout} from "@vkontakte/vkui";
import {observer} from "mobx-react-lite";
import React, {useCallback, useEffect, useRef, useState} from 'react';
import Confetti from "react-confetti";
import useWindowSize from "react-use/lib/useWindowSize";
import {GAME_PANEL, GAME_RESULTS_MODAL} from "../../routing/routing";
import {
    buyHint,
    createGame,
    FINISH_REASON_TIMEOUT,
    finishGame,
    getActiveGame,
    submitWord,
    useHint
} from "../../services/game";
import {add3DailyAttempts, getDailyAttemptsPrice, getPlayerInfo} from "../../services/player";
import GameStore, {maxLetters, normalizeLetters} from "../../store/game";
import {GameBoard} from "./GameBoard";
import {StatusStrip} from "./StatusStrip";
import {HintPanel} from "./HintPanel";
import {LetterPad} from "./LetterPad";
import BackgroundLetters from "../../components/Background/Background";
import {useToast} from "../../context/ToastContext";
import BonusStore from "../../store/bonus";
import {Add3DailyAttemptsBonusPrice} from "../../model/prices";
import {numberWordSuffix} from "../../utils/word";
import AttemptsPanel from "./AttemptsPanel";
import {
    MODAL_CARD_CLASS,
    MODAL_HEADER_CLASS,
    MODAL_PRIMARY_BUTTON_CLASS,
    MODAL_SECONDARY_BUTTON_CLASS,
    MODAL_SUBHEADER_CLASS
} from "../../modals/modalStyles";
import {getBackendErrorMessage} from "../../utils/backendErrorMessages";

// Неизвестная ошибка при загрузке игры
const errUnknownLoadingGame = "ERR_INTERNAL_LOADING_GAME_ERROR"
const errPlayerDailyAttemptsAreEmpty = "ERR_PLAYER_DAILY_ATTEMPTS_ARE_EMPTY"
// Базовый таймер для экрана приветствия, совпадает с серверным дефолтом.
const DEFAULT_GAME_TIME_SECONDS = 60;

// Время (мс), в течение которого серия считается активной
const COMBO_WINDOW_MS = 10000;

const BUTTON_CLASSES = "bg-yellow-400 hover:bg-yellow-500 text-gray-800 border-yellow-400 cursor-pointer";

// Главный компонент игры
export const Game = observer(() => {
    // Роутинг
    const routeNavigator = useRouteNavigator();
    const isFirstPage = useFirstPageCheck();
    const {panel: activePanel} = useActiveVkuiLocation();

    // Состояние компонента
    const [popout, setPopout] = useState(<ScreenSpinner size="large"/>);
    const [isFinishingGame, setIsFinishingGame] = useState(false);
    // Фикс: не даём завершать игру до полной загрузки активной сессии.
    const [isGameSessionLoaded, setIsGameSessionLoaded] = useState(false);
    const [hasShownResultsModal, setHasShownResultsModal] = useState(false);
    const {showErrorToast, showSuccessToast} = useToast();
    const [loadingGameErrorCode, setLoadingGameErrorCode] = useState("");
    const [dailyAttemptPrice, setDailyAttemptPrice] = useState(Add3DailyAttemptsBonusPrice);
    const [isStartingNewGame, setIsStartingNewGame] = useState(false);
    const [shouldShowOnboarding, setShouldShowOnboarding] = useState(false);
    const [isConfettiVisible, setIsConfettiVisible] = useState(false);
    const [confettiKey, setConfettiKey] = useState(0);
    const [isExitAlertOpen, setIsExitAlertOpen] = useState(false);
    // Блокируем повторную отправку слова, пока идёт проверка.
    const [isSubmittingWord, setIsSubmittingWord] = useState(false);
    const allowExitOnceRef = useRef(false);
    const gameBoardScrollRef = useRef(null);
    const confettiTimeoutRef = useRef(null);
    const letters = normalizeLetters(GameStore.letters);
    const availableAttempts = Number(GameStore.dailyAttempts) || 0;
    const {width, height} = useWindowSize();

    // Управляет запуском и остановкой конфетти после завершения игры.
    const triggerConfetti = () => {
        setConfettiKey((prevKey) => prevKey + 1);
        setIsConfettiVisible(true);
        if (confettiTimeoutRef.current) {
            clearTimeout(confettiTimeoutRef.current);
        }
        confettiTimeoutRef.current = setTimeout(() => {
            setIsConfettiVisible(false);
        }, 7000);
    };

    const stopConfetti = () => {
        if (confettiTimeoutRef.current) {
            clearTimeout(confettiTimeoutRef.current);
            confettiTimeoutRef.current = null;
        }
        setIsConfettiVisible(false);
    };

    // Приводим набор букв к ожидаемому размеру клавиатуры
    const normalizeGameLetters = (rawLetters) => {
        const normalized = normalizeLetters(rawLetters);
        if (normalized.length !== maxLetters) {
            return normalizeLetters("");
        }
        return normalized;
    };

    // Инициализирует новую игровую сессию и обновляет состояние игры
    // Восстанавливает историю попыток на основе найденных слов.
    const restoreHistoryFromFoundWords = (game) => {
        if (!Array.isArray(game?.foundWords) || game.foundWords.length === 0) {
            return;
        }

        GameStore.restoreAttemptsFromFoundWords(game.foundWords);
    };

    // Инициализирует новую игровую сессию и обновляет состояние игры
    const startGameSession = (options = {}) => {
        const showSpinner = options.showSpinner !== false;
        setLoadingGameErrorCode("");
        stopConfetti();
        setIsGameSessionLoaded(false);
        if (showSpinner) {
            setPopout(<ScreenSpinner size="large"/>);
        }
        GameStore.flush();

        return createGame().then(resp => {
            const game = resp.data.response;
            hydrateGameState(game);

            // Есть сохранённая игра
            let hasAttemptHistory = false;
            if (game.attempts_json && game.attempts_json.length > 0) {
                hasAttemptHistory = true;
                const attempts = JSON.parse(game.attempts_json);
                attempts.forEach((value, i) => {
                    GameStore.addAttemptToHistory(value);
                    GameStore.incrementAttemptNumber();
                });
            }

            if (game.hints_json && game.hints_json.length > 0) {
                const hints = JSON.parse(game.hints_json);
                if (hints.indexOf("attempt") > -1) {
                    GameStore.addBonusAttempt();
                }
            }

            if (!hasAttemptHistory) {
                restoreHistoryFromFoundWords(game);
            }

            setIsGameSessionLoaded(true);
        }).catch(err => {
            if (err.response !== undefined) {
                const errCode = err.response.data.response.code;
                const errMsg = getBackendErrorMessage(err, "Failed to load game");
                // Синхронизируем попытки, если сервер сообщил об их отсутствии.
                if (errCode === errPlayerDailyAttemptsAreEmpty) {
                    GameStore.setDailyAttempts(0);
                }
                setLoadingGameErrorCode(errCode);
                showErrorToast(errMsg);
                return;
            }

            setLoadingGameErrorCode(errUnknownLoadingGame);
        }).finally(() => {
            setIsGameSessionLoaded(true);
            if (showSpinner) {
                setPopout(null);
            }
        });
    };

    // Запрашивает активную игру и при необходимости запускает новую.
    const loadGameSession = () => {
        setLoadingGameErrorCode("");
        setPopout(<ScreenSpinner size="large"/>);
        setIsGameSessionLoaded(false);

        return getActiveGame().then(resp => {
            const payload = resp?.data?.response ?? resp?.data;
            if (payload?.isActive && payload?.game) {
                GameStore.flush();
                hydrateGameState(payload.game);
                restoreHistoryFromFoundWords(payload.game);
                setShouldShowOnboarding(false);
                setIsGameSessionLoaded(true);
                return;
            }
            // Игра не активна — показываем онбординг перед новым запуском.
            GameStore.flush();
            GameStore.setIsActive(false);
            setShouldShowOnboarding(true);
            setIsGameSessionLoaded(true);
        }).catch(err => {
            const errMsg = getBackendErrorMessage(err, "Failed to load game");
            showErrorToast(errMsg);
        }).finally(() => {
            setIsGameSessionLoaded(true);
            setPopout(null);
        });
    };

    // Запрашивает актуальную цену покупки жизней
    const fetchDailyAttemptPrice = () => {
        getDailyAttemptsPrice().then(resp => {
            const price = resp?.data?.response?.price;
            if (Number.isFinite(price)) {
                setDailyAttemptPrice(price);
            }
        }).catch(() => {
            setDailyAttemptPrice(Add3DailyAttemptsBonusPrice);
        });
    };

    // Обновляет данные игрока для отображения балансов и рекорда
    const refreshPlayerInfo = () => {
        return getPlayerInfo().then(resp => {
            const player = resp.data.response;
            const totalDailyAttempts = player.available_daily_attempts ?? player.daily_attempts ?? 0;
            GameStore.setDailyAttempts(totalDailyAttempts);
            if (player.best_score !== undefined || player.bestScore !== undefined) {
                GameStore.setBestScore(player.best_score ?? player.bestScore);
            }
            if (player.hintBalances || player.hint_balances) {
                GameStore.setPlayerHintBalances(player.hintBalances ?? player.hint_balances);
            }
            if (player.coins !== undefined) {
                BonusStore.setCoins(player.coins);
            }
        }).catch(() => {
            showErrorToast("Failed to refresh player data");
        });
    };

    useEffect(() => {
        const timer = setInterval(() => {
            if (!GameStore.isActive) return;
            const current = GameStore.timeLeft;
            if (current > 0) {
                GameStore.setTimeLeft(current - 1);
            }

            const lastComboTimestamp = GameStore.lastComboTimestamp;
            // Сбрасываем индикацию комбо, если окно времени уже прошло.
            if (lastComboTimestamp && GameStore.comboCount > 0) {
                if (Date.now() - lastComboTimestamp >= COMBO_WINDOW_MS) {
                    GameStore.resetCombo();
                }
            }
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        return () => {
            if (confettiTimeoutRef.current) {
                clearTimeout(confettiTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (GameStore.isActive) {
            setHasShownResultsModal(false);
        }
    }, [GameStore.isActive]);

    useEffect(() => {
        // Для приветственного экрана подтягиваем доступные подсказки из балансов игрока.
        if (!shouldShowOnboarding || GameStore.isActive) {
            return;
        }

        const balances = GameStore.playerHintBalances || {};
        if (Object.keys(balances).length === 0) {
            GameStore.setHintWallet({});
            GameStore.setHintLimits({});
        } else {
            GameStore.setHintWallet(balances);
            GameStore.setHintLimits({});
        }

        // Заполняем таймер, чтобы на приветственном экране был полный индикатор времени.
        const baseTime = GameStore.timeLimitSeconds > 0 ? GameStore.timeLimitSeconds : DEFAULT_GAME_TIME_SECONDS;
        if (GameStore.timeLimitSeconds !== baseTime) {
            GameStore.setTimeLimitSeconds(baseTime);
        }
        if (GameStore.timeLeft <= 0) {
            GameStore.setTimeLeft(baseTime);
        }
    }, [shouldShowOnboarding, GameStore.playerHintBalances, GameStore.isActive]);

    // Скроллим историю попыток вниз после каждого успешного слова.
    useEffect(() => {
        if (shouldShowOnboarding || !gameBoardScrollRef.current) {
            return;
        }

        const scrollTarget = gameBoardScrollRef.current;
        requestAnimationFrame(() => {
            scrollTarget.scrollTop = scrollTarget.scrollHeight;
        });
    }, [GameStore.attemptNumber, GameStore.attempt.length, shouldShowOnboarding]);

    useEffect(() => {
        const timeLeft = Number(GameStore.timeLeft) || 0;
        const hasValidGameID = GameStore.gameID && Number.isFinite(Number(GameStore.gameID));

        if (!isGameSessionLoaded || !hasValidGameID || timeLeft > 0 || !GameStore.isActive || isFinishingGame) {
            return;
        }

        setIsFinishingGame(true);
        GameStore.setIsActive(false);
        GameStore.setTimeLeft(0);
        // Очищаем незавершенную попытку и историю, чтобы полуготовое слово не попадало в список.
        GameStore.addAttemptToHistory(null);
        GameStore.flushAttempt();

        finishGame(GameStore.gameID, FINISH_REASON_TIMEOUT).then(resp => {
            const data = resp?.data?.response ?? resp?.data;
            if (!data) {
                showErrorToast("Failed to finish game");
                return;
            }

            if (data.score !== undefined) {
                GameStore.setScore(data.score);
            }

            if (data.coinsReward !== undefined) {
                GameStore.setEarnedCoins(data.coinsReward);
            }

            // Обновляем состояние комбо в соответствии с финальным ответом сервера.
            if (data.comboCount !== undefined) {
                if (data.comboCount > 0) {
                    GameStore.setComboCount(data.comboCount);
                    GameStore.setLastComboTimestamp(Date.now());
                } else {
                    GameStore.resetCombo();
                }
            }
        }).catch(err => {
            const errMsg = getBackendErrorMessage(err, "Failed to finish game");
            showErrorToast(errMsg);
        }).finally(() => {
            // Синхронизируем жизни после завершения игры
            refreshPlayerInfo();
            setIsFinishingGame(false);
            if (!hasShownResultsModal) {
                setHasShownResultsModal(true);
                triggerConfetti();
                routeNavigator.showModal(GAME_RESULTS_MODAL);
            }
        });
    }, [GameStore.gameID, GameStore.isActive, GameStore.timeLeft, hasShownResultsModal, isFinishingGame, isGameSessionLoaded, routeNavigator, showErrorToast]);

    const normalizeHintState = (rawWallet, rawLimits) => {
        const normalizedLimits = {};
        const normalizedWallet = {};

        // Приводим лимиты к единому виду { max, used }
        if (rawLimits && typeof rawLimits === "object") {
            Object.entries(rawLimits).forEach(([hint, value]) => {
                const max = Number(value?.max ?? value?.total ?? value?.limit ?? 0) || 0;
                let used = Number(value?.used ?? value?.spent ?? 0);
                if (!Number.isFinite(used) || used < 0) {
                    used = 0;
                }
                normalizedLimits[hint] = {max, used};
            });
        }

        // Остатки из кошелька (если пришёл от бэка)
        if (rawWallet && typeof rawWallet === "object") {
            Object.entries(rawWallet).forEach(([hint, available]) => {
                const remaining = Number(available) || 0;
                normalizedWallet[hint] = remaining < 0 ? 0 : remaining;
            });
        }

        // Дополняем кошелек данными из лимитов (если кошелька нет)
        Object.entries(normalizedLimits).forEach(([hint, limit]) => {
            if (normalizedWallet[hint] === undefined) {
                const remaining = Math.max(limit.max - limit.used, 0);
                normalizedWallet[hint] = remaining;
            }
        });

        // Если лимиты не пришли, но есть кошелек — добавим базовые лимиты из кошелька
        if (Object.keys(normalizedLimits).length === 0 && Object.keys(normalizedWallet).length > 0) {
            Object.entries(normalizedWallet).forEach(([hint, remaining]) => {
                normalizedLimits[hint] = {max: remaining, used: 0};
            });
        }

        return {wallet: normalizedWallet, limits: normalizedLimits};
    };

    const hydrateGameState = (game) => {
        if (!game) {
            return;
        }

        const gameId = game.gameId ?? game.id ?? game.GameID ?? game.gameID;
        const parsedId = Number(gameId);
        if (Number.isFinite(parsedId) && parsedId > 0) {
            GameStore.setGameID(parsedId);
        }

        if (game.score !== undefined) {
            GameStore.setScore(game.score);
        }

        const bestScore = game.bestScore ?? game.best_score;
        if (bestScore !== undefined) {
            GameStore.setBestScore(bestScore);
        }

        const combo = game.comboCount ?? game.currentComboCount;
        if (combo !== undefined) {
            GameStore.setComboCount(combo);
        }

        // Восстанавливаем тайминг комбо при возврате в активную игру.
        const lastWordFoundAt = game.lastWordFoundAt ?? game.last_word_found_at;
        if (combo > 0 && lastWordFoundAt) {
            const parsed = Date.parse(lastWordFoundAt);
            if (Number.isFinite(parsed)) {
                if (Date.now() - parsed <= COMBO_WINDOW_MS) {
                    GameStore.setLastComboTimestamp(parsed);
                } else {
                    GameStore.resetCombo();
                }
            }
        }

        if (Array.isArray(game.foundWords)) {
            GameStore.setFoundWords(game.foundWords);
        }

        const hintWallet = game.hintWallet ?? game.hint_wallet;
        const hintLimits = game.hintLimits ?? game.hint_limits;
        const {wallet, limits} = normalizeHintState(hintWallet, hintLimits);
        GameStore.setHintWallet(wallet);
        GameStore.setHintLimits(limits);

        const baseTimeLimit = game.timeLimitSeconds ?? game.time_limit_seconds;
        const extraTime = game.extraTimeSeconds ?? game.extra_time_seconds ?? 0;
        if (baseTimeLimit !== undefined) {
            GameStore.setTimeLimitSeconds(baseTimeLimit + extraTime);
        }

        const serverLeft = game.serverTimeLeft ?? game.timeLeft ?? game.time_left;
        if (serverLeft !== undefined) {
            GameStore.setTimeLeft(serverLeft);
        } else if (baseTimeLimit !== undefined) {
            GameStore.setTimeLeft(baseTimeLimit + extraTime);
        }

        const letters = game.letters ?? game.Letters;
        if (letters !== undefined) {
            const normalized = normalizeGameLetters(letters);
            GameStore.setLetters(normalized.join(""));
        }
    };

    useEffect(() => {
        if (activePanel !== GAME_PANEL) {
            return;
        }
        loadGameSession().finally(() => {
            // Синхронизируем жизни после загрузки, чтобы UI не отставал от бэка.
            refreshPlayerInfo();
        });
        fetchDailyAttemptPrice();
    }, [activePanel]);


    // Находит доступный индекс повторной буквы для ввода с клавиатуры
    const getNextLetterIndex = (letter) => {
        const usedIndexes = new Set(
            GameStore.attempt
                .filter((entry) => entry?.value === letter && Number.isFinite(entry?.keyIndex))
                .map((entry) => entry.keyIndex)
        );

        for (let i = 0; i < letters.length; i += 1) {
            if (letters[i] !== letter) {
                continue;
            }
            if (!usedIndexes.has(i)) {
                return i;
            }
        }

        return null;
    };

    // Проверяет, свободна ли конкретная буква по индексу в текущем наборе
    const isLetterIndexAvailable = (letter, index) => {
        if (!Number.isFinite(index)) {
            return false;
        }
        if (letters[index] !== letter) {
            return false;
        }
        return !GameStore.attempt.some((entry) => entry?.keyIndex === index);
    };

    // Обработчик нажатия на кнопку с буквой на клавиатуре
    const handleChooseLetter = (payload) => {
        if (GameStore.attempt.length >= maxLetters) return;

        let chosenLetter = "";
        let providedIndex = null;
        let source = "keyboard";

        if (typeof payload === "string") {
            chosenLetter = payload;
        } else if (payload && typeof payload.letter === "string") {
            chosenLetter = payload.letter;
            source = payload.source ?? "keyboard";
            providedIndex = Number.isFinite(payload.index) ? payload.index : null;
        }

        if (!chosenLetter) return;

        const targetIndex =
            source === "click" && Number.isFinite(providedIndex)
                ? (isLetterIndexAvailable(chosenLetter, providedIndex) ? providedIndex : null)
                : getNextLetterIndex(chosenLetter);

        if (targetIndex === null) {
            return;
        }

        GameStore.addLetterToAttempt(chosenLetter, targetIndex);
    };

    // Обработчик нажатия на кнопку проверки введённого слова
    const handleDoneButton = () => {
        if (isSubmittingWord) {
            return;
        }
        // Валидация
        if (!GameStore.isActive) {
            return;
        }

        if (GameStore.attempt.length < 2) {
            showErrorToast("Minimum 2 letters");
            return;
        }

        if (!GameStore.gameID || !Number.isFinite(Number(GameStore.gameID))) {
            showErrorToast("Game is not created yet");
            return;
        }

        // Фиксируем ввод на момент отправки, чтобы избежать рассинхрона с UI.
        const attemptSnapshot = GameStore.attempt.map((letter) => ({
            value: letter.value,
            keyIndex: letter.keyIndex,
        }));
        const userWord = attemptSnapshot.map((letter) => letter.value).join("");

        setIsSubmittingWord(true);
        submitWord(GameStore.gameID, userWord).then(resp => {
            const data = resp?.data?.response ?? resp?.data;
            if (!data) {
                showErrorToast("Failed to validate word");
                return;
            }

            if (data.isValid === false) {
                const reasons = {
                    time_expired: "Time is up",
                    already_found: "Word already found",
                    invalid_letters: "Word cannot be formed from available letters",
                    not_in_dictionary: "Word is not in dictionary",
                    game_not_found: "Game not found",
                };
                // Если слово не найдено в словаре или уже было найдено, очищаем ввод и историю текущей попытки.
                if (data.reason === "not_in_dictionary" || data.reason === "already_found") {
                    GameStore.addAttemptToHistory(null);
                    GameStore.flushAttempt();
                }
                const reasonText = reasons[data.reason] ?? "Word was not accepted";
                showErrorToast(reasonText);
                return;
            }

            if (data.newTotalScore !== undefined) {
                GameStore.setScore(data.newTotalScore);
                if (GameStore.bestScore < data.newTotalScore) {
                    GameStore.setBestScore(data.newTotalScore);
                }
            }

            if (data.comboCount !== undefined) {
                GameStore.setComboCount(data.comboCount);
            }
            // Синхронизируем время комбо, если бэк его вернул.
            const lastWordFoundAt = data.lastWordFoundAt ?? data.last_word_found_at;
            const parsedLast = lastWordFoundAt ? Date.parse(lastWordFoundAt) : NaN;
            if (Number.isFinite(parsedLast)) {
                GameStore.setLastComboTimestamp(parsedLast);
            } else {
                GameStore.setLastComboTimestamp(Date.now());
            }

            if (Array.isArray(data.foundWords)) {
                GameStore.setFoundWords(data.foundWords);
            } else {
                GameStore.addFoundWord(userWord);
            }

            if (data.gameId !== undefined || data.id !== undefined) {
                const newId = Number(data.gameId ?? data.id);
                if (Number.isFinite(newId) && newId > 0) {
                    GameStore.setGameID(newId);
                }
            }

            if (data.timeLeft !== undefined) {
                GameStore.setTimeLeft(data.timeLeft);
            }
            if (data.newTimeLeft !== undefined) {
                GameStore.setTimeLeft(data.newTimeLeft);
            }

            const attemptToSave = attemptSnapshot.map((letter) => ({value: letter.value}));
            GameStore.addAttemptToHistory(attemptToSave);
            GameStore.incrementAttemptNumber();
            GameStore.flushAttempt();
        }).catch(err => {
            const errMsg = getBackendErrorMessage(err, "Failed to validate word");
            showErrorToast(errMsg);
        }).finally(() => {
            setIsSubmittingWord(false);
        });
    };

    // Обработчик нажатия на кнопку удаления символа, при вводе пользователем слова
    const handleBackspaceButton = () => {
        if (GameStore.attempt.length === 0) {
            return;
        }

        GameStore.deleteLastLetterFromAttempt();
    };

    // Обработчик нажатия кнопки "Назад"
    const handleBackButton = () => {
        if (!GameStore.isActive) {
            // Сбрасываем текущий ввод, чтобы незавершенное слово не оставалось в истории.
            GameStore.addAttemptToHistory(null);
            GameStore.flushAttempt();
        }
        return isFirstPage ? routeNavigator.push('/') : routeNavigator.replace('/');
    }

    const navigationBlocker = useCallback(({currentLocation, nextLocation}) => {
        if (activePanel !== GAME_PANEL || !GameStore.isActive) {
            return false;
        }

        if (allowExitOnceRef.current) {
            allowExitOnceRef.current = false;
            return false;
        }

        const currentPath = currentLocation?.pathname || "";
        const nextPath = nextLocation?.pathname || "";
        return currentPath === "/game" && nextPath !== "/game";
    }, [activePanel]);

    const exitBlocker = useBlocker(navigationBlocker);

    useEffect(() => {
        if (activePanel !== GAME_PANEL) {
            setIsExitAlertOpen(false);
            return;
        }

        if (exitBlocker?.state === "blocked") {
            setIsExitAlertOpen(true);
        }
    }, [activePanel, exitBlocker?.state]);

    const handleExitStay = () => {
        if (exitBlocker?.reset) {
            exitBlocker.reset();
        }
        setIsExitAlertOpen(false);
    };

    const handleExitLeave = () => {
        // Сбрасываем текущий ввод, чтобы незавершенное слово не оставалось в истории.
        GameStore.addAttemptToHistory(null);
        GameStore.flushAttempt();
        if (exitBlocker?.proceed) {
            setIsExitAlertOpen(false);
            // Пропускаем блокировку при подтвержденном выходе.
            allowExitOnceRef.current = true;
            exitBlocker.proceed();
            return;
        }
        setIsExitAlertOpen(false);
        return isFirstPage ? routeNavigator.push("/") : routeNavigator.replace("/");
    };

    // Локальный попап подтверждения выхода без изменения истории браузера.
    const exitAlert = isExitAlertOpen ? (
        <PopoutWrapper onClick={handleExitStay}>
            <div className="w-full max-w-sm px-4">
                <div className={`flex flex-col gap-3 rounded-xl bg-gray-800 px-5 py-4 ${MODAL_CARD_CLASS}`}>
                    <div className={`text-lg font-semibold ${MODAL_HEADER_CLASS}`}>Exit game?</div>
                    <div className={`text-sm ${MODAL_SUBHEADER_CLASS}`}>
                        The timer will continue. The game will not pause.
                    </div>
                    <div className="flex flex-col gap-2 pt-1">
                        <Button
                            size="l"
                            mode="secondary"
                            className={MODAL_SECONDARY_BUTTON_CLASS}
                            onClick={handleExitLeave}
                        >
                            Exit
                        </Button>
                        <Button
                            size="l"
                            mode="primary"
                            className={MODAL_PRIMARY_BUTTON_CLASS}
                            onClick={handleExitStay}
                        >
                            Stay
                        </Button>
                    </div>
                </div>
            </div>
        </PopoutWrapper>
    ) : null;

    const handleUseHint = (type) => {
        if (!GameStore.gameID) {
            showErrorToast("Game is not created yet");
            return;
        }

        useHint(GameStore.gameID, type).then(resp => {
            const data = resp?.data?.response ?? resp?.data;
            if (!data) {
                showErrorToast("Failed to apply hint");
                return;
            }

            const hintWallet = data.hintWallet ?? data.hint_wallet;
            const hintLimits = data.limits ?? data.hintLimits ?? data.hint_limits;
            const {wallet, limits} = normalizeHintState(hintWallet, hintLimits);
            GameStore.setHintWallet(wallet);
            GameStore.setHintLimits(limits);
            const playerBalances = data.playerHintBalances ?? data.player_hint_balances;
            if (playerBalances) {
                GameStore.setPlayerHintBalances(playerBalances);
            }

            if (data.newTimeLeft !== undefined) {
                GameStore.setTimeLeft(data.newTimeLeft);
            }

            if (data.timeLeft !== undefined) {
                GameStore.setTimeLeft(data.timeLeft);
            }

            const lastWordFoundAt = data.lastWordFoundAt ?? data.last_word_found_at;
            const parsedLast = lastWordFoundAt ? Date.parse(lastWordFoundAt) : NaN;
            if (Number.isFinite(parsedLast)) {
                GameStore.setLastComboTimestamp(parsedLast);
            }

            if (data.letters !== undefined) {
                const normalized = normalizeGameLetters(data.letters);
                GameStore.setLetters(normalized.join(""));
            }
            if (type === "letters_reroll") {
                // Сбрасываем текущую попытку, чтобы слово не конфликтовало с новым набором.
                GameStore.flushAttempt();
                GameStore.addAttemptToHistory(null);
            }

            showSuccessToast("Hint used");
        }).catch(err => {
            const errMsg = getBackendErrorMessage(err, "Failed to apply hint");
            showErrorToast(errMsg);
        });
    };

    const handleBuyHint = (type, options = {}) => {
        // Возвращаем промис, чтобы подсказку можно было применять сразу после покупки.
        return buyHint(type, {gameId: GameStore.gameID}).then(resp => {
            const player = resp?.data?.response ?? resp?.data;
            if (!player) {
                showErrorToast("Failed to buy hint");
                return;
            }

            const playerBalances = player.hintBalances ?? player.hint_balances;
            const shouldUpdateBalance = !options?.skipBalanceUpdate;
            if (playerBalances && shouldUpdateBalance) {
                GameStore.setPlayerHintBalances(playerBalances);
            }

            if (player.coins !== undefined) {
                BonusStore.setCoins(player.coins);
            }

            if (!options?.skipBalanceUpdate) {
                showSuccessToast("Hint purchased");
            }
        }).catch(err => {
            const errMsg = getBackendErrorMessage(err, "Failed to buy hint");
            showErrorToast(errMsg);
            // Прерываем цепочку, чтобы не запускать использование при ошибке покупки.
            throw err;
        });
    };

    // Запускает новую игру после завершения текущей
    const handleStartNewGame = () => {
        if (availableAttempts <= 0 || isStartingNewGame) {
            return;
        }
        // Перед новой игрой всегда показываем приветственный экран.
        GameStore.flush();
        GameStore.setIsActive(false);
        setShouldShowOnboarding(true);
        refreshPlayerInfo();
    };

    // Старт первой игры после онбординга
    const handleStartFirstGame = () => {
        if (isStartingNewGame) {
            return;
        }
        if (availableAttempts <= 0) {
            showErrorToast("No lives available");
            return;
        }
        setShouldShowOnboarding(false);
        setIsStartingNewGame(true);
        GameStore.setDailyAttempts(Math.max(availableAttempts - 1, 0));
        startGameSession().finally(() => {
            refreshPlayerInfo();
            setIsStartingNewGame(false);
        });
    };

    // Покупка жизней после окончания игры
    const handleBuyDailyAttempts = () => {
        if (BonusStore.coins < dailyAttemptPrice) {
            showErrorToast("Not enough coins");
            return;
        }

        add3DailyAttempts().then(resp => {
            const player = resp.data.response;
            const totalDailyAttempts = player.available_daily_attempts ?? player.daily_attempts ?? 0;
            GameStore.setDailyAttempts(totalDailyAttempts);
            if (player.coins !== undefined) {
                BonusStore.setCoins(player.coins);
            }
            showSuccessToast("Lives restored");
        }).catch(err => {
            const errMsg = getBackendErrorMessage(err, "Failed to buy lives");
            showErrorToast(errMsg);
        });
    };

    return <>
        <BackgroundLetters/>
        {isConfettiVisible && (
            <Confetti
                key={confettiKey}
                recycle={false}
                width={width}
                height={height}
            />
        )}

        <PanelHeader
            className="sticky top-0 z-20"
            before={<PanelHeaderBack className={"[&>svg]:p-0"} onClick={handleBackButton}/>}
            separator={null}
        >
            Wordmania
        </PanelHeader>

        <SplitLayout popout={exitAlert || popout}>
            <SplitCol>
                {/* Контейнер на всю ширину max-w-lg повторяет единый шаблон страниц */}
                <div
                    className="flex flex-col p-4 items-center mx-auto w-full max-w-lg h-[calc(100svh-56px)] overflow-hidden">
                    <div className="flex flex-col gap-3 w-full h-full">

                        {/* У игрока закончились дневные попытки */}
                        {loadingGameErrorCode === errPlayerDailyAttemptsAreEmpty && (
                            <AttemptsPanel
                                dailyAttemptPrice={dailyAttemptPrice}
                                canBuy={BonusStore.coins >= dailyAttemptPrice}
                                onBuy={handleBuyDailyAttempts}
                                availableAttempts={availableAttempts}
                                onStart={handleStartNewGame}
                                title="No lives left"
                                description="Buy lives and continue without waiting."
                                withCountdown
                                onCannotBuy={() => showErrorToast("Not enough coins")}
                            />
                        )}

                        {/* Другая ошибка загрузки игры */}
                        {loadingGameErrorCode === errUnknownLoadingGame && <>
                            <div className="flex flex-row px-4 py-2 items-center justify-center">
                                <div className="flex w-full bg-gray-800 rounded px-4 py-6 items-center gap-3">
                                    <p className="bold text-gray-300">
                                        An unknown error occurred while loading the game. We are already working on a fix.
                                    </p>
                                </div>
                            </div>
                        </>}

                        {/* Ошибок загрузки игры нет */}
                        {loadingGameErrorCode === "" && (
                            <div className="flex flex-col gap-3 flex-1 min-h-0">
                                <StatusStrip/>
                                <HintPanel onUseHint={handleUseHint} onBuyHint={handleBuyHint}/>

                                {/* История попыток прокручивается отдельно, клавиатура всегда прижата к низу */}
                                <div className="flex flex-col flex-1 min-h-0 px-4">
                                    <div
                                        ref={gameBoardScrollRef}
                                        className="flex-1 min-h-0 overflow-y-auto pb-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                                    >
                                        {!isGameSessionLoaded ? null : shouldShowOnboarding ? (
                                            <div
                                                className="flex flex-col items-center justify-center text-center bg-gray-800 rounded-xl px-5 py-6 gap-3">
                                                <div className="text-lg bold text-gray-100">
                                                    Welcome to Wordmania
                                                </div>
                                                <div className="text-sm text-gray-300">
                                                    Build words from available letters, earn points, and keep your combo alive before time runs out.
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    The timer starts after pressing the button.
                                                </div>
                                                <Button
                                                    size="s"
                                                    mode="primary"
                                                    stretched
                                                    className={`${BUTTON_CLASSES}`}
                                                    onClick={handleStartFirstGame}
                                                    disabled={isStartingNewGame}
                                                >
                                                    <span className={"text-sm"}>Start</span>
                                                </Button>
                                            </div>
                                        ) : (
                                            <GameBoard/>
                                        )}
                                    </div>

                                    {!isGameSessionLoaded || shouldShowOnboarding ? null : GameStore.isActive ? (
                                        <div className="pt-2">
                                            <LetterPad
                                                onLetter={handleChooseLetter}
                                                onEnter={handleDoneButton}
                                                onBackspace={handleBackspaceButton}
                                            />
                                        </div>
                                    ) : (
                                        <div className="flex flex-row w-full py-2 items-center justify-center">
                                            <div
                                                className="flex w-full bg-gray-800 rounded-xl px-4 py-6 items-center justify-center">
                                                <div className="flex flex-col items-center gap-3 w-full max-w-sm">
                                                    <p className="text-xl bold text-gray-300 mb-2">Game over 🎉</p>

                                                    <Separator/>

                                                    {availableAttempts > 0 ? (
                                                        <>
                                                            <Button
                                                                size="s"
                                                                mode="primary"
                                                                stretched
                                                                className={`${BUTTON_CLASSES}`}
                                                                onClick={handleStartNewGame}
                                                            >
                                                                Start new game
                                                            </Button>
                                                            <div className="text-sm text-gray-500 text-center">
                                                                Start a new game and beat your result 😉
                                                            </div>
                                                            <div className="text-xs text-gray-300">
                                                                {availableAttempts} {availableAttempts === 1 ? "life remains" : "lives remain"}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <AttemptsPanel
                                                                dailyAttemptPrice={dailyAttemptPrice}
                                                                canBuy={BonusStore.coins >= dailyAttemptPrice}
                                                                onBuy={handleBuyDailyAttempts}
                                                                availableAttempts={availableAttempts}
                                                                onStart={handleStartNewGame}
                                                                title="No lives left"
                                                                description="Restore lives and continue without waiting."
                                                                withCountdown={false}
                                                                onCannotBuy={() => showErrorToast("Not enough coins")}
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </SplitCol>
        </SplitLayout>
    </>
});
